import React, { useMemo } from "react";
import moment from "moment-timezone";
import { Drawer, Button } from "@mui/material";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import {
  CircleOffIcon,
  CircleArrowUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "../../../components/commonUI/Icons/SvgIcon";
import PropTypes from "prop-types";

const DriverPenaltiesTab = ({
  issueResolutions,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onReset,
  filters,
  sort,
  onSort,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  inputValue,
  setInputValue,
  state,
  toggleDrawer,
  DriverPenaltiesProps,
}) => {
  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "sorting-icon sorting-icon-up";
    } else if (sort === `${key}=-1`) {
      return "sorting-icon sorting-icon-down";
    }
    return "";
  };

  const getJobTypeDisplay = (jobType) => {
    const jobTypeMap = {
      getitems: "Get items",
      housemove: "House move",
      senditems: "Send items",
      wasteDisposal: "Waste Disposal",
    };
    return jobTypeMap[jobType] || jobType;
  };

  const renderPenaltyRow = (issueResolution, index) => {
    const statusMap = {
      exempt: {
        label: "Waived",
        badge: "badge-secondary",
        icon: <CircleOffIcon />,
      },
      "not exempt": {
        label: "Applied",
        badge: "badge-danger",
        icon: <CircleArrowUpIcon />,
      },
      pending: {
        label: "Pending",
        badge: "badge-warning",
        icon: <ClockIcon />,
      },
      approved: {
        label: "Approved",
        badge: "badge-success",
        icon: <CheckCircleIcon />,
      },
      rejected: {
        label: "Rejected",
        badge: "badge-danger",
        icon: <XCircleIcon />,
      },
      closed: {
        label: "Cancelled by client",
        badge: "badge-danger",
      },
    };

    const status = issueResolution?.status;
    const statusData = statusMap[status?.toLowerCase()];

    const cancellationRatePercentage =
      (issueResolution?.driverDetails?.cancellationRate /
        issueResolution?.driverDetails?.bookings) *
      100;

    return (
      <tr key={index}>
        <td className="text-capitalize">
          {issueResolution?.driverDetails?.firstname}
        </td>
        <td>
          {issueResolution?.driverDetails?.cancellationRate}/
          {issueResolution?.driverDetails?.bookings} bookings <br />
          <span className="text-xs text-gray-600">
            {cancellationRatePercentage?.toFixed(2)}%
          </span>
        </td>
        <td>
          <span className="u-before-icon text-capitalize">
            &nbsp;&nbsp;{issueResolution?.cancellationDetails?.reason}
          </span>
        </td>
        <td>
          {moment(issueResolution?.cancellationDetails?.cancelledOn).format(
            "DD-MM-YYYY"
          )}
          <br />
          {moment(issueResolution?.cancellationDetails?.cancelledOn).format(
            "HH:mm A"
          )}
        </td>
        <td>
          {statusData && (
            <span className={`badge ${statusData?.badge}`}>
              {statusData?.icon}
              <span className="ml-1">{statusData?.label}</span>
            </span>
          )}
        </td>
        <td>
          <span>{getJobTypeDisplay(issueResolution?.jobDetails?.jobType)}</span>
          <br />
          <span className="text-xs text-gray-600">
            {issueResolution?.jobDetails?.shortId}
          </span>
        </td>
        <td>
          <strong> &nbsp;&nbsp; £</strong>
          {issueResolution?.jobDetails?.bookedFor / 100 || 0}
        </td>
        <td>
          {moment(issueResolution?.jobDetails?.bookedOn).format("DD-MM-YYYY")}
          <br />
          <span className="text-xs text-gray-600">
            {moment(issueResolution?.jobDetails?.bookedOn).format("HH:mm")}
          </span>
        </td>
        <td>
          {moment(issueResolution?.jobDetails?.scheduledFor).format(
            "DD-MM-YYYY"
          )}
          <br />
          <span className="text-xs text-gray-600">
            {moment(issueResolution?.jobDetails?.scheduledFor).format("HH:mm")}
          </span>
        </td>
        <td>
          <Button
            className="btn btn-sm btn-secondary"
            onClick={toggleDrawer("right", true, "details", issueResolution)}
          >
            Review
          </Button>
        </td>
      </tr>
    );
  };

  const columns = useMemo(
    () => [
      { key: "driver", label: "Driver", sortable: true, sortKey: "firstnameSorter" },
      {
        key: "cancellations",
        label: "Cancellations",
        sortable: true,
        sortKey: "cancellationRateSorter",
      },
      { key: "reason", label: "Reason", sortable: true, sortKey: "reasonSorter" },
      {
        key: "cancelledOn",
        label: "Cancelled On",
        sortable: true,
        sortKey: "cancelledOnSorter",
      },
      { key: "status", label: "Status", sortable: true, sortKey: "statusSorter" },
      { key: "job", label: "Job", sortable: false },
      { key: "bookedFor", label: "Booked For", sortable: true, sortKey: "bookedFor" },
      { key: "bookedOn", label: "Booked On", sortable: true, sortKey: "bookedOnSorter" },
      {
        key: "scheduledFor",
        label: "Scheduled for",
        sortable: true,
        sortKey: "scheduledForSorter",
      },
      { key: "actions", label: "Actions", sortable: false },
    ],
    []
  );

  return (
    <>
      <SearchFilterBar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        searchPlaceholder="Client, job ID, or reason..."
        onReset={onReset}
        filters={filters}
      />

      <DataTable
        data={issueResolutions}
        columns={columns}
        renderRow={renderPenaltyRow}
        onSort={onSort}
        getSortClass={getSortClass}
        emptyMessage="No Driver Penalties Found"
        colSpan={10}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={onPageChange}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={onPageSizeChange}
      />

      <Drawer
        anchor="right"
        open={state.right}
        onClose={toggleDrawer("right", false)}
        ModalProps={{
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          },
        }}
        className="issues-drawer"
      >
        {DriverPenaltiesProps("right")}
      </Drawer>
    </>
  );
};

DriverPenaltiesTab.propTypes = {
  issueResolutions: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  sort: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  inputValue: PropTypes.number.isRequired,
  setInputValue: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  DriverPenaltiesProps: PropTypes.func.isRequired,
};

export default DriverPenaltiesTab;
